import React, { useCallback, useEffect, useState } from 'react';
import { Switch } from '../base/Switch';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { Badge } from '../base';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { setSkills, setSkillEnabled } from '../../redux/features/app/toolSlice';

export const SkillPanel: React.FC = () => {
  const isElectron = typeof window !== 'undefined' && window.electron?.isElectron;
  const dispatch = useAppDispatch();
  const skills = useAppSelector((state: RootState) => state.tools.skills);
  const [loading, setLoading] = useState(false);
  const [isToggling, setIsToggling] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!window.electron?.skillList) return;
    setLoading(true);
    try {
      const list = await window.electron.skillList();
      dispatch(setSkills(list ?? []));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    if (isElectron) void refresh();
  }, [isElectron, refresh]);

  // 스킬 추가 (zip)
  const handleInstall = async () => {
    if (!window.electron?.skillInstall) return;
    const res = await window.electron.skillInstall();
    if (res?.canceled) return;
    if (!res?.ok) {
      alert(`스킬 추가 실패: ${res?.error ?? '알 수 없는 오류'}`);
      return;
    }
    await refresh();
  };

  // 활성화/비활성화 토글
  const handleToggle = async (skillId: string) => {
    const skill = skills.find((s) => s.id === skillId);
    if (!skill) return;

    const newEnabled = !skill.enabled;
    setIsToggling(skillId);

    dispatch(setSkillEnabled({ skillId, enabled: newEnabled }));

    try {
      const res = await window.electron?.skillSetEnabled?.(skillId, newEnabled);
      if (res && !res.ok) {
        throw new Error(res.error ?? '알 수 없는 오류');
      }
    } catch (error) {
      console.error('Failed to update skill enabled:', error);
      dispatch(setSkillEnabled({ skillId, enabled: !newEnabled }));
    } finally {
      setIsToggling(null);
    }
  };

  const handleDelete = async (skillId: string) => {
    if (!window.electron?.skillDelete) return;
    if (!window.confirm(`'${skillId}' 스킬을 삭제할까요?`)) return;
    await window.electron.skillDelete(skillId);
    await refresh();
  };

  if (!isElectron) {
    return (
      <div className="tool-settings-content">
        <div className="tool-empty-state">
          <p className="tool-empty-message">스킬 기능은 데스크탑 앱에서만 사용할 수 있습니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tool-settings-content">
      <div className="tool-settings-header">
        <h3 className="tool-settings-title">스킬</h3>
        <Button
          size="small"
          theme="primary"
          icon="add"
          className="btn-add-tool"
          onClick={() => {
            void handleInstall();
          }}
        >
          스킬 추가
        </Button>
      </div>

      <div className="tool-settings-list">
        {loading && skills.length === 0 && (
          <div className="tool-empty-state">
            <p className="tool-empty-message">불러오는 중…</p>
          </div>
        )}

        {!loading && skills.length === 0 && (
          <div className="tool-empty-state">
            <p className="tool-empty-message">스킬이 없습니다. 스킬 추가 버튼을 눌러 추가해주세요.</p>
          </div>
        )}

        {skills.length > 0 && (
          <div className="tool-list">
            {skills.map((s) => (
              <div key={s.id} className="tool-item">
                <div className="tool-item-left">
                  <div className="tool-icon">
                    <Badge accent='normal'>{s.name.slice(0, 1).toUpperCase()}</Badge>
                  </div>
                  <div className="tool-info">
                    <div className="tool-name">{s.name}</div>
                    {s.description && <div className="tool-desc">{s.description}</div>}
                  </div>
                </div>
                <div className="tool-item-right">
                  <Switch
                    checked={s.enabled}
                    disabled={isToggling === s.id}
                    onChange={() => {
                      void handleToggle(s.id);
                    }}
                  />
                  <button
                    className="btn-more"
                    onClick={() => {
                      void handleDelete(s.id);
                    }}
                  >
                    <Icon name="delete" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SkillPanel;
